import { Client, User, ClientStatus } from "api/types";
import { reactivateArchiveAccount } from "api/user";
import classNames from "classnames";
import React, { useState } from "react";
import Avatar from "react-avatar";
import { useNavigate } from "react-router-dom";
import { getDifferenceForDates } from "utils/util";
import Badge, { BadgeProps } from "./Badge";
import Button from "./Button";
import { Icon } from "./Icon";
import { handleGenericResponse } from "./utility";

interface ClientDetailProps {
  client: Client;
}

export enum ExpiryStatus {
  VALID = "VALID",
  ABOUT_TO_EXPIRE = "ABOUT_TO_EXPIRE",
  ABOUT_TO_EXPIRE_IN_1_MONTH = "ABOUT_TO_EXPIRE_IN_1_MONTH",
  ABOUT_TO_EXPIRE_IN_2_MONTH = "ABOUT_TO_EXPIRE_IN_2_MONTH",
  EXPIRED = "Expired",
  NA = "NA",
}

export const checkExpiry = (expiryDate: string | undefined) => {
  if (!expiryDate) {
    return ExpiryStatus.NA;
  }
  const { diffInDays } = getDifferenceForDates(new Date(expiryDate));
  if (diffInDays < 0) {
    return ExpiryStatus.EXPIRED;
  } else if (diffInDays < 31) {
    return ExpiryStatus.ABOUT_TO_EXPIRE_IN_1_MONTH;
  } else if (diffInDays < 91) {
    return ExpiryStatus.ABOUT_TO_EXPIRE_IN_2_MONTH;
  }
  return ExpiryStatus.VALID;
};

export const getPropsForExpiry = (
  expiryStatus: ExpiryStatus,
  status: string
): BadgeProps => {
  if (expiryStatus === ExpiryStatus.EXPIRED) {
    return {
      iconName: "info",
      message: status,
      className:
        "bg-red-100 !text-red-800 text-xs leading-4 font-medium uppercase !rounded-none",
      statusClassName: "!m-0 !mr-1",
      isIconOnRight: true,
      iconClassName: "text-red-700",
      size: 10,
    };
  }
  if (expiryStatus === ExpiryStatus.ABOUT_TO_EXPIRE_IN_2_MONTH) {
    return {
      iconName: "info",
      message: status,
      className:
        "bg-yellow-100 text-yellow-800 text-xs leading-4 font-medium uppercase !rounded-none",
      statusClassName: "!m-0 !mr-1",
      isIconOnRight: true,
      iconClassName: "!text-yellow-700",
      size: 10,
    };
  }
  if (expiryStatus === ExpiryStatus.ABOUT_TO_EXPIRE_IN_1_MONTH) {
    return {
      iconName: "info",
      message: status,
      className:
        "bg-orange-100 text-orange-700 text-xs leading-4 font-medium uppercase !rounded-none",
      statusClassName: "!m-0 !mr-1",
      isIconOnRight: true,
      iconClassName: "!text-orange-600",
      size: 10,
    };
  }
  return {
    message: status,
    className:
      "text-xs leading-4 font-medium tracking-wider uppercase !text-gray-700 !px-0 !rounded-none",
    statusClassName: "!m-0",
    size: 10,
  };
};

export const getContentByExpiry = (expiryStatus: ExpiryStatus) => {
  if (expiryStatus === ExpiryStatus.ABOUT_TO_EXPIRE_IN_1_MONTH) {
    return "Document expires within 1 month ";
  }
  if (expiryStatus === ExpiryStatus.ABOUT_TO_EXPIRE_IN_2_MONTH) {
    return "Document expires within 2-3 months";
  }
  if (expiryStatus === ExpiryStatus.EXPIRED) {
    return "Document has expired";
  }
  return "";
};

export const getAvatarName = (person: Client | User) => {
  if (person.firstName && person.lastName) {
    return `${person.firstName} ${person.lastName}`;
  }
  if (person.username) {
    return person.username;
  }
  return person.email;
};

export const getProfileStatusBadgeProps = (status: ClientStatus): BadgeProps => {
  switch (status) {
    case ClientStatus.Active:
      return {
        iconName: "check",
        message: status,
        className: "bg-green-100 text-green-800",
      };
    case ClientStatus.Discharged:
      return {
        iconName: "solidCheck",
        message: status.split("_").join(" "),
        className: "bg-purple-100 text-purple-800",
        iconClassName: "text-purple-800",
        buttonClassName: "-mt-1",
        size: 12,
      };
    case ClientStatus.Deceased:
      return {
        iconName: "inProgress",
        message: status.split("_").join(" "),
        className: "bg-blue-100 text-blue-800",
        iconClassName: "text-blue-800",
        size: 12,
      };
    case ClientStatus.Onboarding:
      return {
        message: status.split("_").join(" "),
        className: "bg-blue-100 text-blue-800",
        iconClassName: "text-blue-800",
        size: 12,
      };
    case ClientStatus.Voluntary_Discharge:
      return {
        message: status.split("_").join(" "),
        iconName: "invited",
        className: "bg-pink-100 text-pink-800",
      };
    case ClientStatus.Changed_Care_Provider:
      return {
        iconName: "invited",
        message: status,
        className: "bg-pink-100 text-pink-800",
      };
    case ClientStatus.Agreement_Expired_No_Longer_Eligible:
      return {
        iconName: "invited",
        message: status.split("_").join(" "),
        className: "bg-pink-100 text-pink-800",
      };
    case ClientStatus["Suspended/On_Hold"]:
      return {
        iconName: "invited",
        message: status.split("_").join(" "),
        className: "bg-pink-100 text-pink-800",
      };
    default:
      return {
        iconName: "hazardWarning",
        message: "Cannot Complete",
        className: "bg-yellow-100 text-yellow-800",
        iconClassName: "text-yellow-800",
        size: 12,
      };
  }
};

const ClientDetail: React.FC<ClientDetailProps> = ({
  client,
}: ClientDetailProps) => {
  const [isShowMoreDetails, setIsShowMoreDetails] = useState(false);
  const navigate = useNavigate();
  const clientStatus = client?.clientProfile ? client.clientProfile.profileStatus : ClientStatus.Onboarding;
  
  
  return (
    <div
      className={classNames(
        "my-5 bg-white shadow rounded-md",
        client.archivedAt && "!bg-gray-200 !shadow-sm"
      )}
    >
      <div
        className={classNames(
          "p-6 border-b border-gray-200 flex items-center justify-between flex-col md:flex-row",
          client.archivedAt && "!border-gray-300"
        )}
      >
        <div className="flex items-start justify-start w-full">
          <Avatar
            name={getAvatarName(client)}
            size="40"
            round
            textSizeRatio={2.5}
            className="!h-10 !w-10 !text-base !leading-6 !font-normal !text-primary-900"
            color="#D1F5FC"
            fgColor="#194860"
            src={client.avatar}
          />
          <div className="flex flex-col items-start ml-4 w-36">
            <span className="text-sm leading-5 font-semibold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap text-left w-36">{`${client.title} ${client.firstName} ${client.lastName}`}</span>
            <span className="text-sm leading-5 font-normal text-gray-700   overflow-hidden text-ellipsis whitespace-nowrap text-left w-36">
              HCP Level: {client.clientProfile ? client.clientProfile.assignedHcpLevel  : "N/A"}
            </span>
          </div>
          <div className="flex flex-col items-start ml-6  w-36">
            <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
              PROFILE Status
            </span>
            <Badge {...getProfileStatusBadgeProps(clientStatus)} />
          </div>

          <div className="flex flex-col justify-start items-start flex-wrap ml-6  w-28">
            <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
              Phone:
            </span>
            <span className="text-sm leading-5 font-semibold text-gray-900">
              {client.mobile || "N/A"}
            </span>
          </div>
          <div className="flex flex-col justify-start items-start flex-wrap ml-6 flex-1">
            <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
              Suburb:
            </span>
            <span className="text-sm leading-5 font-semibold text-gray-900">
              {client.suburb || "N/A"}
            </span>
          </div>
          <div className="space-x-2 flex items-center justify-center">
            {client.archivedAt && (
              <Button
                variant="primary"
                size="xs"
                className="space-x-2 flex justify-center items-center mr-3"
                onClick={async () => {
                  const response = await reactivateArchiveAccount(client.id);
                  handleGenericResponse(
                    response,
                    "Account reactivated successfully"
                  );
                  // queryClient.invalidateQueries([`/api/user/admin/clients`]);
                }}
              >
                <span>Re-activate account</span>
              </Button>
            )}
            <Badge
              message={
                client.createdAccount ? "Created Account" : "Invited Account"
              }
              className="bg-blue-100 text-blue-800"
            />
            {client.archivedAt && (
              <Badge
                iconName="archive"
                message="Archived"
                className="bg-pink-100 text-pink-800"
              />
            )}
          </div>
        </div>
      </div>

      {isShowMoreDetails && (
        <>
          <div className="p-6 flex items-center justify-between flex-wrap md:flex-nowrap">            
            <div className="flex flex-col items-start md:w-1/5 w-1/2">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Received Taxi Card
              </span>
              <span className="w-full bg-red-100 !text-red-800 text-xs leading-4 font-medium uppercase !rounded-none">
                NO
              </span>
            </div>
            <div className="flex flex-col items-start md:w-1/5 w-1/2">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Medicare Expiry
              </span>
              <span className="text-sm leading-5 font-medium text-gray-900">
                N/A
              </span>
            </div>
            <div className="flex flex-col items-start md:w-1/5 w-1/2">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Insurance Expiry
              </span>
              <span className="text-sm leading-5 font-medium text-gray-900">
                N/A
              </span>
            </div>
            <div className="flex flex-col items-start md:w-1/5 w-1/2">
              <span className="text-xs leading-4 font-medium tracking-wider uppercase text-gray-700">
                Ambulance Expiry
              </span>
              <span className="text-sm leading-5 font-medium text-gray-900">
                N/A
              </span>
            </div>
          </div>
          {/* {client.failureReasons?.length > 0 && (
            <div className="px-5 py-4">
              <div className="flex items-center w-full">
                <span className="text-base leading-5 font-medium mr-1">
                  Failure reasons
                </span>
                <span className="border border-gray-300 flex-1"></span>
              </div>
              <div className="flex justify-start items-start mt-5">
                {client.failureReasons.map((reason) => (
                  <Badge
                    message={reason.split("_").join(" ")}
                    iconName="hazardWarning"
                    className="bg-yellow-100 text-yellow-800 mr-4"
                    iconClassName="text-yellow-800"
                    size={12}
                  />
                ))}
              </div>
            </div>
          )} */}
        </>
      )}

      <div
        className={classNames(
          "flex py-3 px-6 justify-between items-center border border-gray-200 bg-gray-50 rounded-b-md ",
          client.archivedAt && "!bg-gray-100 !border-t-gray-300"
        )}
      >
        <Button
          variant="gray"
          className="flex items-center cursor-pointer space-x-2"
          onClick={() => setIsShowMoreDetails(!isShowMoreDetails)}
        >
          <Icon
            name="downChevron"
            size={16}
            className={classNames(isShowMoreDetails && "animate rotate-180")}
          />
          <span className="text-sm leading-5 font-semibold">
            {isShowMoreDetails ? "Show less" : "Show more"}
          </span>
        </Button>
        <Button
          variant="text"
          onClick={() => navigate(`/client/view-profile/${client.id}`)}
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default ClientDetail;
